import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  type FeedbackRef,
  frownIcon,
  frustratedIcon,
  grinIcon,
  neutralIcon,
  smileIcon,
  UserFeedback,
} from '@github-ui/user-feedback'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {RefObject} from 'react'

export const ISSUE_FEEDBACK_MAX_LENGTH = 2000

export const ISSUE_FEEDBACK_RATING_OPTIONS = [
  {
    name: 'Hate',
    value: 1 as const,
    icon: frustratedIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Dislike',
    value: 2 as const,
    icon: frownIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Neutral',
    value: 3 as const,
    icon: neutralIcon,
    color: 'dissatisfied' as const,
  },
  {
    name: 'Like',
    value: 4 as const,
    icon: smileIcon,
    color: 'verySatisfied' as const,
  },
  {
    name: 'Love',
    value: 5 as const,
    icon: grinIcon,
    color: 'verySatisfied' as const,
  },
]

export type IssueFeedbackRating = (typeof ISSUE_FEEDBACK_RATING_OPTIONS)[number]['value']
export type IssueFeedbackDialogRef = FeedbackRef<IssueFeedbackRating>

/** Sends feedback to `/issues/feedback`. Returns whether it was saved. */
export async function sendIssueFeedback({
  subject,
  rating,
  content,
}: {
  subject: string
  rating: IssueFeedbackRating | null
  content: string
}): Promise<boolean> {
  try {
    const response = await verifiedFetchJSON('/issues/feedback', {
      method: 'POST',
      body: {subject, rating, content, hostname: window.location.hostname, path: window.location.pathname},
    })
    return response.ok
  } catch {
    return false
  }
}

export interface IssueFeedbackDialogProps {
  title?: string
  subject?: string
  onClose?: () => void
  returnFocusRef?: RefObject<HTMLElement | null>
}

export const IssueFeedbackDialog = ({
  ref,
  title = 'Give feedback on Issues',
  subject = 'issues_feedback',
  returnFocusRef,
  onClose,
}: IssueFeedbackDialogProps & {ref?: React.RefObject<IssueFeedbackDialogRef | null>}) => {
  const showFeedback = isFeatureEnabled('issues_react_ui_feedback')

  const onSubmit = async (rating: IssueFeedbackRating | null, text: string): Promise<string[]> => {
    const errors = []

    if (rating == null) {
      errors.push('Please select a rating.')
    }

    if (text.length > ISSUE_FEEDBACK_MAX_LENGTH) {
      errors.push(`Please keep your feedback within ${ISSUE_FEEDBACK_MAX_LENGTH} characters or less.`)
    }

    if (errors.length > 0) {
      return errors
    }

    if (!(await sendIssueFeedback({subject, rating, content: text}))) {
      errors.push('An error occurred while submitting your feedback.')
    }

    return errors
  }

  if (!showFeedback) {
    return null
  }

  return (
    <UserFeedback
      ref={ref}
      title={title}
      options={ISSUE_FEEDBACK_RATING_OPTIONS}
      onSubmit={onSubmit}
      onClose={onClose}
      returnFocusRef={returnFocusRef}
    />
  )
}
IssueFeedbackDialog.displayName = 'IssueFeedbackDialog'
